import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Coins } from 'lucide-react';
import {
  ChartBarIcon,
  CalendarDaysIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  SparklesIcon,
  Cog6ToothIcon,
  FlagIcon,
  UsersIcon,
  PhoneIcon,
  ClockIcon,
  BoltIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import PageLoader from '../../components/PageLoader';
import { userAssistantConfigService, type GlobalVoiceOptions } from '../../services/userAssistantConfigService';
import { useRunCampaignPage } from './useRunCampaignPage';
import { TemperaturePill, DispositionBadge, GoalOutcomeIcon, FollowUpIndicator } from '../../components/intelligence';
import type { CallSummary, Lead, RunProgress } from '../RunProgressPage/runProgressTypes';
import MultiSelectFilter from '../../components/filters/MultiSelectFilter';
import RunCampaignModal from '../LeadsPage/RunCampaignModal';
import { formatUtcToBrisbane, getDefaultBrisbaneInput } from '../../utils/brisbaneDateTime';
import './RunDetailsTabs.css';

const TAB_IDS = ['overview', 'agent-config'] as const;
type TabId = (typeof TAB_IDS)[number];

function getInitialTab(searchParams: URLSearchParams): TabId {
  const t = searchParams.get('tab');
  if (t === 'overview' || t === 'agent-config') return t;
  return 'overview';
}

const TEMP_ORDER = ['hot', 'warm', 'lukewarm', 'cold', 'dead'] as const;
const TEMP_COLORS: Record<string, string> = {
  hot: 'bg-red-500', warm: 'bg-orange-500', lukewarm: 'bg-yellow-500', cold: 'bg-blue-500', dead: 'bg-slate-500',
};
const KNOWN_TEMPERATURES = new Set<string>(TEMP_ORDER);

const RunCampaignPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>(() => getInitialTab(searchParams));
  const [globalVoiceOptions, setGlobalVoiceOptions] = useState<GlobalVoiceOptions | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const scheduleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setActiveTab(getInitialTab(searchParams)); }, [searchParams]);
  useEffect(() => { userAssistantConfigService.getGlobalVoiceOptions().then(setGlobalVoiceOptions).catch(() => setGlobalVoiceOptions(null)); }, []);
  useEffect(() => {
    if (!scheduleModalOpen) return;
    scheduleInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setScheduleModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [scheduleModalOpen]);

  const hook = useRunCampaignPage();
  const {
    runId, run, leads, callSummaries, executionStatus, loading, error,
    autoRefresh, setAutoRefresh, creditStatus,
    toast, showToast, startRun, saveSchedule, cancelSchedule,
    scheduledAtInput, setScheduledAtInput, scheduleValidationError,
    getStatusColor,
  } = hook;

  const setTab = (tab: TabId) => { setActiveTab(tab); setSearchParams({ tab }, { replace: true }); };

  if (loading && !run) return <PageLoader message="Loading campaign..." />;

  if (error) return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="relative px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">
          <h2 className="text-lg font-medium mb-2">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    </div>
  );

  if (!run) return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="relative px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="bg-yellow-500/20 border border-yellow-400 text-yellow-100 px-4 py-3 rounded-lg">
          <h2 className="text-lg font-medium mb-2">Campaign Not Found</h2>
          <p>The requested campaign could not be found.</p>
        </div>
      </div>
    </div>
  );

  const pendingActionsCount = run.follow_ups_pending ?? 0;
  const tempBreakdown = run.temperature_breakdown;
  const knownTempTotal = TEMP_ORDER.reduce((total, temp) => total + (tempBreakdown?.[temp] ?? 0), 0);
  const minScheduleInput = getDefaultBrisbaneInput(0);

  const openDateTimePicker = () => {
    const input = scheduleInputRef.current;
    if (!input) return;
    if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
      (input as HTMLInputElement & { showPicker: () => void }).showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const handleConfirmSchedule = async () => {
    const result = await saveSchedule();
    if (result.ok) {
      setScheduleModalOpen(false);
      // Trigger toast after modal-close state applies to ensure visibility.
      window.setTimeout(() => {
        showToast('success', result.message || 'Schedule updated.');
      }, 0);
    }
  };

  const scheduleModal =
    scheduleModalOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-[2px]"
        role="presentation"
        onClick={() => !loading && setScheduleModalOpen(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="run-schedule-modal-title"
          className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900/95 shadow-2xl shadow-black/40 p-5 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <h3 id="run-schedule-modal-title" className="text-lg font-semibold text-white">
              {run.status === 'scheduled' ? 'Update schedule' : 'Schedule campaign'}
            </h3>
            <button
              type="button"
              aria-label="Close"
              disabled={loading}
              onClick={() => setScheduleModalOpen(false)}
              className="rounded-lg p-1 text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-40 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-1.5">Schedule time</label>
            <div className="flex items-center gap-2">
              <input
                ref={scheduleInputRef}
                type="datetime-local"
                value={scheduledAtInput}
                onChange={(e) => setScheduledAtInput(e.target.value)}
                min={minScheduleInput}
                disabled={loading}
                className="schedule-datetime-input flex-1 rounded-lg border border-white/15 bg-slate-950/80 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={openDateTimePicker}
                disabled={loading}
                aria-label="Open date and time picker"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 bg-slate-950/80 text-white hover:bg-slate-900 disabled:opacity-50"
              >
                <CalendarDaysIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">Brisbane time (UTC+10). Future times only.</p>
            {scheduleValidationError && (
              <p className="mt-2 text-xs text-amber-300">{scheduleValidationError}</p>
            )}
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
            <button
              type="button"
              disabled={loading}
              onClick={() => setScheduleModalOpen(false)}
              className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium text-gray-300 border border-white/20 rounded-lg hover:bg-white/5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmSchedule}
              disabled={loading || !!scheduleValidationError}
              className="inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : run.status === 'scheduled' ? 'Update schedule' : 'Confirm schedule'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  const toastPortal =
    toast &&
    createPortal(
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]">
        <div className={`flex items-start space-x-3 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-md ${
          toast.type === 'success' ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-100'
          : toast.type === 'error' ? 'bg-red-500/15 border-red-400/40 text-red-100'
          : 'bg-blue-500/15 border-blue-400/40 text-blue-100'
        }`}>
          <div className="mt-0.5">
            {toast.type === 'success' && <CheckCircleIcon className="h-5 w-5" />}
            {toast.type === 'error' && <XCircleIcon className="h-5 w-5" />}
            {toast.type === 'info' && <SparklesIcon className="h-5 w-5" />}
          </div>
          <div className="text-sm font-medium leading-5">{toast.message}</div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {scheduleModal}
      {toastPortal}

      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
      </div>

      <div className="relative px-2 sm:px-3 lg:px-8 py-3 sm:py-4 lg:py-6 xl:py-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
            <div>
              <div className="flex items-center space-x-2 sm:space-x-3 mb-1 sm:mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
                </div>
                <h1 className="text-lg sm:text-xl lg:text-3xl xl:text-4xl font-bold text-slate-50 truncate max-w-[280px] sm:max-w-none">
                  {run.name}
                </h1>
                <RunStatusBadge status={run.status} />
              </div>
              <p className="text-gray-300 text-xs sm:text-sm">
                Campaign &middot; Created {new Date(run.created_at).toLocaleDateString()}
                {run.agent_name && <> &middot; Agent: <span className="text-white">{run.agent_name}</span></>}
              </p>
              {run.status === 'scheduled' && run.scheduled_at && (
                <p className="text-cyan-300 text-xs sm:text-sm mt-1">
                  Scheduled for {formatUtcToBrisbane(run.scheduled_at)}
                </p>
              )}
              {run.start_source === 'scheduled' && run.status !== 'scheduled' && (
                <p className="text-cyan-300 text-xs sm:text-sm mt-1">
                  Started via schedule
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-2 sm:mt-0 sm:items-end">
              {(run.status === 'draft' || run.status === 'scheduled') && (
                <div className="flex flex-col gap-2 sm:items-end">
                  <p className="text-[11px] text-slate-400">
                    {run.status === 'scheduled'
                      ? `Scheduled for ${formatUtcToBrisbane(run.scheduled_at ?? null)}`
                      : 'Not scheduled yet'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setScheduleModalOpen(true)}
                      disabled={loading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-100 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ClockIcon className="h-3.5 w-3.5" />
                      {run.status === 'scheduled' ? 'Update Schedule' : 'Schedule'}
                    </button>
                    {run.status === 'scheduled' && (
                      <button
                        onClick={cancelSchedule}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Cancel Schedule
                      </button>
                    )}
                  </div>
                </div>
              )}
              {(run.status === 'draft' || run.status === 'scheduled') && (
                <button
                  onClick={startRun}
                  disabled={loading || creditStatus?.credit_status === 'exhausted'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <PlayIcon className="h-3.5 w-3.5" />
                  {creditStatus?.credit_status === 'exhausted' ? 'No Credits' : run.status === 'scheduled' ? 'Run Now' : 'Start Campaign'}
                </button>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <nav className="run-details-tabs" aria-label="Campaign sections">
            {[
              { id: 'overview' as const, label: 'Overview', icon: PlayIcon },
              { id: 'agent-config' as const, label: 'Agent Configuration', icon: Cog6ToothIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                className={`run-details-tab ${activeTab === tab.id ? 'run-details-tab-active' : ''}`}
              >
                <tab.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Execution status bar (only while running) */}
            {executionStatus && (run.status === 'running' || run.status === 'paused') && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-700/50 bg-slate-800/40 text-xs">
                <BoltIcon className="h-4 w-4 text-cyan-400 animate-pulse" />
                <span className="text-slate-300">Live:</span>
                <span className="text-blue-400">Dispatched {executionStatus.dispatched}</span>
                <span className="text-emerald-400">Completed {executionStatus.completed}</span>
                <span className="text-red-400">Failed {executionStatus.failed}</span>
                <label className="ml-auto flex items-center text-slate-400">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="mr-1.5 text-cyan-500 focus:ring-cyan-500 rounded"
                  />
                  Auto-refresh
                </label>
              </div>
            )}

            {/* Metric tiles - same data as list view */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3">
              <MetricTile label="Total Leads" value={run.total_leads} icon={UsersIcon} color="text-cyan-400" bg="bg-cyan-500/20" />
              <MetricTile label="Answered" value={run.completed_calls} icon={PhoneIcon} color="text-emerald-400" bg="bg-emerald-500/20" subtitle={(run.inbound_completed_calls ?? 0) > 0 ? `${run.inbound_completed_calls} inbound` : undefined} />
              <MetricTile label="Failed" value={run.failed_calls} icon={XCircleIcon} color="text-red-400" bg="bg-red-500/20" />
              <MetricTile label="Pending" value={(run.pending_calls ?? 0) + (run.in_progress_calls ?? 0)} icon={ClockIcon} color="text-amber-400" bg="bg-amber-500/20" />
              <MetricTile label="Credits Used" value={run.total_credits_used ?? 0} icon={Coins} color="text-orange-400" bg="bg-orange-500/20" />
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3">
                <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">Lead Temp</p>
                {tempBreakdown && knownTempTotal > 0 ? (
                  <div className="space-y-1.5">
                    <div className="flex rounded-full h-2 overflow-hidden bg-slate-700/50">
                      {TEMP_ORDER.map((t) => {
                        const count = tempBreakdown[t] ?? 0;
                        if (count === 0) return null;
                        return <div key={t} className={TEMP_COLORS[t]} style={{ width: `${(count / knownTempTotal) * 100}%` }} title={`${t}: ${count}`} />;
                      })}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-slate-500">—</p>
                )}
              </div>
              <button
                type="button"
                onClick={pendingActionsCount > 0 ? () => navigate('/follow-ups') : undefined}
                disabled={pendingActionsCount === 0}
                className={`rounded-xl border border-slate-700/50 bg-slate-800/40 p-3 flex items-center gap-2.5 w-full text-left transition-colors ${
                  pendingActionsCount > 0 ? 'cursor-pointer hover:bg-slate-800/70' : 'cursor-default'
                }`}
              >
                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${pendingActionsCount > 0 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                  <FlagIcon className={`h-3.5 w-3.5 ${pendingActionsCount > 0 ? 'text-amber-400' : 'text-slate-500'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Actions</p>
                  <p className={`text-sm font-semibold tabular-nums ${pendingActionsCount > 0 ? 'text-amber-400' : 'text-white'}`}>{pendingActionsCount}</p>
                </div>
              </button>
            </div>

            {/* Progress bar */}
            {run.total_leads > 0 && (
              <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-slate-400">Progress</span>
                  <span className="text-xs text-slate-400">
                    {run.completed_calls + run.failed_calls} / {run.total_leads} processed
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-cyan-500 h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${((run.completed_calls + run.failed_calls) / run.total_leads) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Unified Leads & Calls table */}
            <UnifiedLeadsTable
              leads={leads}
              callSummaries={callSummaries}
              runId={runId!}
              navigate={navigate}
              getStatusColor={getStatusColor}
              showToast={showToast}
            />
          </div>
        )}

        {activeTab === 'agent-config' && (
          <AgentConfigurationTab run={run} globalVoiceOptions={globalVoiceOptions} />
        )}
      </div>
    </div>
  );
};

/* ---- Metric Tile ---- */
function MetricTile({ label, value, icon: Icon, color, bg, subtitle }: { label: string; value: number; icon: React.ElementType; color: string; bg: string; subtitle?: string }) {
  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-3 flex items-center gap-2.5">
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}>
        <Icon className={`h-3.5 w-3.5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-white tabular-nums">{value.toLocaleString()}</p>
        {subtitle && <p className="text-[9px] text-teal-400/80 leading-tight">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ---- Run Status Badge ---- */
function RunStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'bg-gray-500/20 text-gray-300' },
    scheduled: { label: 'Scheduled', cls: 'bg-cyan-500/20 text-cyan-300' },
    running: { label: 'Running', cls: 'bg-emerald-500/20 text-emerald-300' },
    paused: { label: 'Paused', cls: 'bg-amber-500/20 text-amber-300' },
    completed: { label: 'Completed', cls: 'bg-blue-500/20 text-blue-300' },
    stopped: { label: 'Stopped', cls: 'bg-red-500/20 text-red-300' },
  };
  const info = map[status] ?? { label: status, cls: 'bg-gray-500/20 text-gray-300' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${info.cls}`}>{info.label}</span>;
}

/* ---- Unified Leads & Calls Table ---- */
interface UnifiedTableProps {
  leads: Lead[];
  callSummaries: CallSummary[];
  runId: string;
  navigate: ReturnType<typeof useNavigate>;
  getStatusColor: (s: string) => string;
  showToast: (type: 'success' | 'error' | 'info', message: string) => void;
}

function UnifiedLeadsTable({
  leads, callSummaries, runId, navigate, getStatusColor, showToast,
}: UnifiedTableProps) {
  const [tempFilter, setTempFilter] = useState<string[]>([]);
  const [dispositionFilter, setDispositionFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [goalFilter, setGoalFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [showRunCampaignModal, setShowRunCampaignModal] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const summaryByLead = new Map<string, CallSummary>();
  for (const cs of callSummaries) {
    const existing = summaryByLead.get(cs.lead_id);
    if (!existing) {
      summaryByLead.set(cs.lead_id, cs);
      continue;
    }
    const existingIsOutbound = existing.call_direction !== 'inbound';
    const csIsOutbound = cs.call_direction !== 'inbound';
    if (csIsOutbound && !existingIsOutbound) {
      summaryByLead.set(cs.lead_id, cs);
    } else if (csIsOutbound === existingIsOutbound && new Date(cs.timestamp) > new Date(existing.timestamp)) {
      summaryByLead.set(cs.lead_id, cs);
    }
  }

  const dispositionOptions = Array.from(
    new Set(
      leads
        .map((lead) => summaryByLead.get(lead.id)?.disposition ?? lead.current_disposition)
        .filter((v): v is string => !!v)
    )
  ).sort();
  const statusOptions = Array.from(new Set(leads.map((lead) => lead.call_status).filter(Boolean))).sort();
  const goalOptions = Array.from(
    new Set(
      leads
        .map((lead) => summaryByLead.get(lead.id)?.goal_outcome)
        .filter((v): v is string => !!v)
    )
  ).sort();
  const temperatureOptions = ['hot', 'warm', 'lukewarm', 'cold', 'dead'];

  const filtered = leads.filter((lead) => {
    const cs = summaryByLead.get(lead.id);
    const rawLeadTemp = cs ? cs.lead_temperature : lead.current_temperature;
    const leadTemp = (rawLeadTemp ?? '').trim().toLowerCase();
    const disposition = cs?.disposition ?? lead.current_disposition ?? null;
    const goal = cs?.goal_outcome ?? null;
    const query = searchTerm.trim().toLowerCase();

    if (tempFilter.length > 0 && (!leadTemp || !KNOWN_TEMPERATURES.has(leadTemp) || !tempFilter.includes(leadTemp))) return false;
    if (dispositionFilter.length > 0 && (!disposition || !dispositionFilter.includes(disposition))) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(lead.call_status)) return false;
    if (goalFilter.length > 0 && (!goal || !goalFilter.includes(goal))) return false;
    if (query) {
      const haystack = `${lead.name} ${lead.phone} ${lead.email ?? ''}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const startIdx = (page - 1) * perPage;
  const pageLeads = filtered.slice(startIdx, startIdx + perPage);
  const isAllOnPageSelected = pageLeads.length > 0 && pageLeads.every((lead) => selectedLeadIds.has(lead.id));
  const hasActiveFilters =
    tempFilter.length > 0 ||
    dispositionFilter.length > 0 ||
    statusFilter.length > 0 ||
    goalFilter.length > 0 ||
    searchTerm.trim().length > 0;
  const selectedLeads = useMemo(
    () => leads.filter((lead) => selectedLeadIds.has(lead.id)),
    [leads, selectedLeadIds]
  );
  const setRunCampaignToast = (t: { type: 'success' | 'error'; message: string } | null) => {
    if (!t) return;
    showToast(t.type, t.message);
  };

  useEffect(() => {
    setSelectedLeadIds(new Set());
  }, [tempFilter, dispositionFilter, statusFilter, goalFilter, searchTerm]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleSelection = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const selectAllOnPage = (selected: boolean) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        pageLeads.forEach((lead) => next.add(lead.id));
      } else {
        pageLeads.forEach((lead) => next.delete(lead.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedLeadIds(new Set());

  return (
    <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50 overflow-hidden">
      <div className="p-2 sm:p-3 lg:p-4 border-b border-slate-700/30 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm sm:text-base font-semibold text-white">Calls</h2>
          <span className="text-xs text-slate-400">{filtered.length} lead{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Name, phone, or email..."
              className="w-full rounded-md bg-slate-800/60 border border-slate-700/60 text-slate-200 text-xs px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </label>
          <MultiSelectFilter
            label="Lead Temp"
            selectedValues={tempFilter}
            onChange={(values) => { setTempFilter(values); setPage(1); }}
            options={temperatureOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
          <MultiSelectFilter
            label="Disposition"
            selectedValues={dispositionFilter}
            onChange={(values) => { setDispositionFilter(values); setPage(1); }}
            options={dispositionOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
          <MultiSelectFilter
            label="Call Status"
            selectedValues={statusFilter}
            onChange={(values) => { setStatusFilter(values); setPage(1); }}
            options={statusOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
          <MultiSelectFilter
            label="Goal"
            selectedValues={goalFilter}
            onChange={(values) => { setGoalFilter(values); setPage(1); }}
            options={goalOptions.map((value) => ({ value, label: value.replace(/_/g, ' ') }))}
          />
        </div>
      </div>

      {selectedLeadIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 xl:px-4 py-3 bg-cyan-500/10 border-b border-cyan-500/20">
          <span className="text-sm text-cyan-200">
            <strong>{selectedLeadIds.size}</strong> lead{selectedLeadIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              className="px-3 py-1.5 text-xs sm:text-sm text-gray-300 hover:text-white border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={() => setShowRunCampaignModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg text-sm font-medium"
            >
              Run Campaign
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-800/30">
            <tr>
              <th className="w-10 px-2 sm:px-3 py-2 text-center">
                <input
                  type="checkbox"
                  className="accent-cyan-500"
                  checked={isAllOnPageSelected}
                  onChange={(e) => selectAllOnPage(e.target.checked)}
                  aria-label="Select all leads on this page"
                />
              </th>
              <th className="px-2 sm:px-3 lg:px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Lead</th>
              <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Lead Temp</th>
              <th className="hidden sm:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Disposition</th>
              <th className="hidden sm:table-cell px-2 sm:px-3 py-2 text-center text-xs font-medium text-slate-400 uppercase">Goal</th>
              <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Call Status</th>
              <th className="hidden md:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Date</th>
              <th className="hidden xl:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Credits</th>
              <th className="hidden lg:table-cell px-2 sm:px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/30">
            {pageLeads.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">
                  {hasActiveFilters
                    ? 'No leads match current filters'
                    : 'No leads in this campaign'}
                </td>
              </tr>
            ) : pageLeads.map((lead) => {
              const cs = summaryByLead.get(lead.id);
              const hasCalled = !!cs;
              const durationSec = cs?.call_metadata?.duration_seconds;
              return (
                <tr
                  key={lead.id}
                  className={`hover:bg-slate-800/30 transition-colors ${hasCalled ? 'cursor-pointer' : ''}`}
                  onClick={hasCalled ? () => navigate(`/runs/${runId}/calls/${cs!.id}`) : undefined}
                >
                  <td
                    className="px-2 sm:px-3 py-2.5 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="accent-cyan-500"
                      checked={selectedLeadIds.has(lead.id)}
                      onChange={() => toggleSelection(lead.id)}
                      aria-label={`Select ${lead.name}`}
                    />
                  </td>
                  <td className="px-2 sm:px-3 lg:px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-white font-medium truncate max-w-[140px]">{lead.name}</span>
                      <FollowUpIndicator followUp={cs?.follow_up_action} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                      <span>{lead.phone}</span>
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 py-2.5">
                    <TemperaturePill temperature={cs ? cs.lead_temperature : lead.current_temperature} />
                  </td>
                  <td className="hidden sm:table-cell px-2 sm:px-3 py-2.5">
                    <DispositionBadge disposition={cs?.disposition ?? lead.current_disposition} />
                  </td>
                  <td className="hidden sm:table-cell px-2 sm:px-3 py-2.5 text-center">
                    {cs ? (
                      <span title={getGoalTooltip(cs)}>
                        <GoalOutcomeIcon outcome={cs.goal_outcome} />
                      </span>
                    ) : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-2 sm:px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getStatusColor(lead.call_status)}`}>
                      {lead.call_status === 'inbound_completed' ? 'Completed (Inbound)' : lead.call_status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    {cs?.timestamp ? new Date(cs.timestamp).toLocaleDateString() : '—'}
                  </td>
                  <td className="hidden xl:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    {cs?.credits_used != null ? cs.credits_used.toLocaleString() : '—'}
                  </td>
                  <td className="hidden lg:table-cell px-2 sm:px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    {durationSec != null ? `${Math.floor(durationSec / 60)}m ${Math.floor(durationSec % 60)}s` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length > perPage && (
        <div className="px-3 sm:px-4 py-2 border-t border-slate-700/30 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {startIdx + 1}–{Math.min(startIdx + perPage, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-2 py-1 text-xs bg-slate-700/50 text-white rounded hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed">Prev</button>
            <span className="px-2 text-xs text-gray-300">Page {page}/{totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-2 py-1 text-xs bg-slate-700/50 text-white rounded hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      <RunCampaignModal
        isOpen={showRunCampaignModal}
        onClose={() => setShowRunCampaignModal(false)}
        selectedLeads={selectedLeads}
        setToast={setRunCampaignToast}
      />
    </div>
  );
}

function getGoalTooltip(cs: CallSummary): string {
  const details = Array.isArray(cs.goal_detail) ? cs.goal_detail : [];
  if (details.length > 0) {
    const met = details.filter((g) => g?.achieved).length;
    if (met === 0) return "No goals met";
    return `${met} of ${details.length} goals met`;
  }
  if (cs.goal_outcome === "achieved") return "Goals achieved";
  if (cs.goal_outcome === "partial") return "Partially achieved";
  if (cs.goal_outcome === "no_progress") return "No goals met";
  if (cs.goal_outcome === "not_applicable") return "Goals not applicable";
  return "Goal status unavailable";
}

/* ---- Agent Configuration Tab ---- */
function getVoiceDisplayLabel(voiceId: string | null | undefined, globalVoiceOptions: GlobalVoiceOptions | null): string | null {
  if (!voiceId || !voiceId.trim()) return null;
  if (!globalVoiceOptions) return voiceId.trim();
  const male = (globalVoiceOptions.global_voice_id_male ?? '').trim();
  const female = (globalVoiceOptions.global_voice_id_female ?? '').trim();
  if (male && voiceId === male) return 'Male';
  if (female && voiceId === female) return 'Female';
  return voiceId.trim();
}

function AgentConfigurationTab({ run, globalVoiceOptions }: { run: RunProgress; globalVoiceOptions?: GlobalVoiceOptions | null }) {
  const voiceDisplay = getVoiceDisplayLabel(run.voice_id ?? null, globalVoiceOptions ?? null);
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: 'Assistant ID', value: run.assistant_id || null },
    { label: 'Agent name', value: run.agent_name ?? null },
    { label: 'Voice', value: voiceDisplay },
    { label: 'Context', value: run.context ?? null },
    { label: 'Goals', value: run.goals ?? null },
    { label: 'Target audience', value: run.target_audience ?? null },
    { label: 'Call objective', value: run.call_objective ?? null },
    { label: 'Success metrics', value: run.success_metrics ?? null },
  ];
  const hasCustom = Array.isArray(run.custom_fields) && run.custom_fields.length > 0;

  return (
    <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50">
      <div className="p-2 sm:p-3 lg:p-4 border-b border-slate-700/30 flex items-center gap-2">
        <Cog6ToothIcon className="h-5 w-5 text-cyan-400" />
        <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-white">Agent Configuration</h2>
      </div>
      <div className="p-3 sm:p-4 lg:p-5 space-y-4">
        <p className="text-gray-400 text-sm">
          Configuration used for this campaign. Empty fields were not set when the campaign was created.
        </p>
        <div className="grid gap-3 sm:gap-4">
          {rows.map((row) => {
            const hasValue = row.value != null && String(row.value).trim() !== '';
            return (
              <div key={row.label} className="rounded-lg bg-slate-800/50 border border-slate-700/40 p-3">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{row.label}</p>
                <p className={`text-sm whitespace-pre-wrap break-words ${hasValue ? 'text-white' : 'text-slate-500 italic'}`}>
                  {hasValue ? row.value : 'Not set'}
                </p>
              </div>
            );
          })}
        </div>
        <div className="rounded-lg bg-slate-800/50 border border-slate-700/40 p-3">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Custom fields</p>
          {hasCustom ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {run.custom_fields!.map((f, i) => (
                <div key={i} className="flex flex-col gap-0.5">
                  <span className="text-xs text-slate-500">{f.title ?? '—'}</span>
                  <span className="text-sm text-white">{f.value ?? '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 italic">None</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RunCampaignPage;
